import React, { useState, useContext, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext, API, getFamilyCode } from "@/App";
import { getAnimal, GOLD_COIN_URL } from "@/data/gameData";
import { Home, BookOpen, Archive, Map, Store, Users, LifeBuoy } from "lucide-react";
import HomeDashboard from "@/pages/HomeDashboard";
import Library from "@/pages/Library";
import MyCollection from "@/pages/MyCollection";
import TreasureMap from "@/pages/TreasureMap";
import StorePage from "@/pages/Store";
import axios from "axios";

const TABS = [
  { id: "home", label: "Home", Icon: Home },
  { id: "library", label: "Library", Icon: BookOpen },
  { id: "collection", label: "My Tome", Icon: Archive },
  { id: "map", label: "Map", Icon: Map },
  { id: "store", label: "Store", Icon: Store },
];

export default function MainLayout() {
  const navigate = useNavigate();
  const { currentUser, account, logout } = useContext(UserContext);
  const [activeTab, setActiveTab] = useState("home");
  const [stats, setStats] = useState(null);
  const isFamilyChild = account?.account_type === "family_child";
  const isParent = account?.role === "parent";

  const refreshStats = useCallback(() => {
    if (currentUser) {
      axios.get(`${API}/users/${currentUser.id}/stats`).then(r => setStats(r.data)).catch(() => {});
    }
  }, [currentUser]);

  useEffect(() => { refreshStats(); }, [refreshStats]);

  const animal = getAnimal(stats?.avatar_animal || currentUser?.avatar_animal);

  const renderPage = () => {
    switch (activeTab) {
      case "home": return <HomeDashboard onNavigate={setActiveTab} stats={stats} refreshStats={refreshStats} onLogout={logout} />;
      case "library": return <Library refreshStats={refreshStats} />;
      case "collection": return <MyCollection refreshStats={refreshStats} />;
      case "map": return <TreasureMap stats={stats} />;
      case "store": return <StorePage stats={stats} refreshStats={refreshStats} />;
      default: return <HomeDashboard onNavigate={setActiveTab} stats={stats} refreshStats={refreshStats} onLogout={logout} />;
    }
  };

  const profileClick = () => {
    if (isFamilyChild) navigate(`/join/${getFamilyCode() || ""}`);
    else navigate("/account");
  };

  return (
    <div className="min-h-screen pb-20" style={{ background: "#FFF9F0" }} data-testid="main-layout">
      <div className="sticky top-0 z-40" style={{ background: "linear-gradient(180deg, #5C3D2E 0%, #8C5E3C 100%)" }} data-testid="top-bar">
        <div className="max-w-2xl mx-auto px-4 py-2.5 flex items-center justify-between">
          <button onClick={profileClick} className="flex items-center gap-2.5" data-testid="top-bar-profile" title={isFamilyChild ? "Switch adventurer" : "Account settings"}>
            <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0" style={{ border: "2px solid #FFC107", background: `${animal?.color || '#F97316'}20` }}>
              {animal?.image ? <img src={animal.image} alt={animal.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-lg" style={{ background: `${animal?.color}30` }}>{animal?.emoji}</div>}
            </div>
            <span className="font-heading text-sm font-semibold text-white truncate max-w-[80px]">{currentUser?.name}</span>
            {isFamilyChild && <Users className="w-4 h-4 text-yellow-200" />}
          </button>

          <div className="flex items-center gap-3">
            {isParent && <button onClick={() => navigate("/parent")} title="Family dashboard" aria-label="Open family dashboard" className="flex items-center gap-1 text-xs font-semibold text-yellow-100 hover:text-white"><Users className="w-4 h-4" /><span className="hidden sm:inline">Family</span></button>}
            <a href="mailto:support@treasuretome.com?subject=Treasure%20Tome%20Help" title="Get help" aria-label="Email Treasure Tome support" className="text-white/80 hover:text-white"><LifeBuoy className="w-4 h-4" /></a>
            <div className="flex items-center gap-1" data-testid="top-bar-coins"><img src={GOLD_COIN_URL} alt="coin" className="w-5 h-5 object-contain rounded-full" /><span className="font-heading text-xs font-bold text-yellow-300">{stats?.gold_coins || 0}</span></div>
            <div className="flex items-center gap-1" data-testid="top-bar-streak"><span className="text-sm">🔥</span><span className="font-heading text-xs font-bold text-orange-300">{stats?.streak_days || 0}</span></div>
            <div className="flex items-center gap-1" data-testid="top-bar-memorized"><span className="text-sm">📚</span><span className="font-heading text-xs font-bold text-blue-300">{stats?.memorized || 0}</span></div>
            <div className="px-2 py-0.5 rounded-full text-[10px] font-heading font-bold" style={{ background: "#FFC10730", color: "#FFC107", border: "1px solid #FFC10750" }} data-testid="top-bar-rank">{stats?.rank?.name || "Cabin Boy"}</div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-5 pb-4">{renderPage()}</div>

      <nav className="bottom-nav fixed bottom-0 left-0 right-0 z-50 pb-8" data-testid="bottom-nav">
        <div className="max-w-2xl mx-auto flex items-center justify-around py-2">
          {TABS.map(({ id, label, Icon }) => (
            <button key={id} onClick={() => setActiveTab(id)} className={`bottom-nav-item flex flex-col items-center gap-0.5 px-3 py-1.5 ${activeTab === id ? "active" : ""}`} style={{ color: activeTab === id ? "#D97706" : "#7A5946" }} data-testid={`nav-${id}`}>
              <Icon className="w-5 h-5" strokeWidth={activeTab === id ? 2.5 : 2} />
              <span className="text-[10px] font-semibold">{label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
